/**
 * Story lane projections (P707 + plan 9.5 + P213).
 *
 * TEMPORARY SHIM: the bridge's decode/map whitelist is owned by the
 * integration lane this batch. Until their one-block diff lands (proposed in
 * the lane's integration patch), UiState carries no typed `story`/`lifelog`
 * fields, so these readers go through an explicit cast and return null.
 * When the bridge projection lands, `sim.story`/`sim.lifelog` are the same
 * objects and the readers keep working unchanged. Nothing here derives
 * client-side; every value comes from the sim's collectUiState.
 */
import type { UiState } from './bridge';

export type UiReserveState = 'locked' | 'available' | 'active' | 'won';

export interface UiStoryReserve {
  id: string;
  scenario: string;
  region: string;
  /** Display name. The approved campaign labels live in data/campaign.json;
   *  the sim falls back to the scenario name for older data. */
  name: string;
  state: UiReserveState;
}

export interface UiStoryMoment { id: string; label: string; firedTick: number | null }

export interface UiJournalRequirement { label: string; met: boolean; current?: number; currentText?: string; target?: number; children?: UiJournalRequirement[] }
export interface UiStory {
  objectiveTotals?: {completed:number;expired:number};
  objectives?: Array<{id:string;name:string;kind:string;hoursLeft:number;requirement:UiJournalRequirement|null;rewardCashCents:number;reputation:{track:string;delta:number}|null}>;
  journal?: {id: string; name: string; status: string; daysLeft: number | null; goal: UiJournalRequirement | null; fail: UiJournalRequirement | null} | null;
  campaign: { reserves: UiStoryReserve[]; complete: boolean };
  moments: UiStoryMoment[];
}

export interface UiLifeLogEvent {
  id: string;
  tick: number;
  day: number;
  type: string;
  message: string;
  refId?: string;
}

export interface UiLifeLogEntry { id: number; animalId: string; eventRef: string }

export interface UiLifeLog { events: UiLifeLogEvent[]; entries: UiLifeLogEntry[] }

export function storyOf(sim: UiState): UiStory | null {
  const raw = (sim as unknown as { story?: UiStory | null }).story;
  return raw ?? null;
}

export function lifeLogOf(sim: UiState): UiLifeLog | null {
  const raw = (sim as unknown as { lifelog?: UiLifeLog | null }).lifelog;
  return raw ?? null;
}

/** Entries for one animal, newest last, with their stored events resolved.
 *  An entry whose event is missing from the projection is dropped (the sim
 *  guarantees resolution; this is a display guard, not a repair). */
export function lifeLogFor(log: UiLifeLog | null, animalId: string): Array<{ entry: UiLifeLogEntry; event: UiLifeLogEvent }> {
  if (log === null) return [];
  const byId = new Map(log.events.map((e) => [e.id, e]));
  const out: Array<{ entry: UiLifeLogEntry; event: UiLifeLogEvent }> = [];
  for (const entry of log.entries) {
    if (entry.animalId !== animalId) continue;
    const event = byId.get(entry.eventRef);
    if (event !== undefined) out.push({ entry, event });
  }
  return out;
}
